import React, { useContext, useState } from "react";
import {
  ModalButtonsContainer,
  ModalContext,
  TextButton,
} from "@SeemplicityDev/shared";
import AgentConfigsList from "./AgentConfigsList";
import AnalystConfigForm from "./AnalystConfigForm";
import {
  useAnalystInstancesConfigs,
  useAvailableIntegrations,
} from "../../../AnalystRuns/AnalystRunsApi";
import {
  INTEGRATION_CATEGORY,
  type AnalystInstanceBase,
} from "../../../AnalystRuns/types";
import type { AccountConfigSectionProps } from "../accountConfigTypes";

type AgentConfigsView = "list" | "create" | "edit";

const AgentConfigsSection: React.FC<AccountConfigSectionProps> = ({
  accountUuid,
}) => {
  const { closeModal } = useContext(ModalContext);
  const [view, setView] = useState<AgentConfigsView>("list");
  const [selected, setSelected] = useState<AnalystInstanceBase | null>(null);

  const {
    data: configsData,
    isLoading: isConfigsLoading,
    isError: isConfigsError,
  } = useAnalystInstancesConfigs(accountUuid);
  const { data: integrationsData, isLoading: isIntegrationsLoading } =
    useAvailableIntegrations(accountUuid);

  const existingConfigs = configsData?.agent_instances ?? [];

  const availableIntegrations = integrationsData?.integrations ?? [];
  const ticketProviderOptions = availableIntegrations.filter(
    (i) => i.integration_category === INTEGRATION_CATEGORY.TICKET
  );
  const datasourceOptions = availableIntegrations.filter(
    (i) => i.integration_category === INTEGRATION_CATEGORY.DATASOURCE
  );

  const backToList = () => {
    setSelected(null);
    setView("list");
  };

  if (view === "create") {
    return (
      <AnalystConfigForm
        mode="create"
        accountUuid={accountUuid}
        ticketProviderOptions={ticketProviderOptions}
        datasourceOptions={datasourceOptions}
        existingConfigs={existingConfigs}
        integrationsLoading={isIntegrationsLoading}
        onCancel={backToList}
        onSuccess={backToList}
      />
    );
  }

  if (view === "edit" && selected) {
    return (
      <AnalystConfigForm
        mode="edit"
        accountUuid={accountUuid}
        initialConfig={selected}
        ticketProviderOptions={ticketProviderOptions}
        datasourceOptions={datasourceOptions}
        existingConfigs={existingConfigs}
        integrationsLoading={isIntegrationsLoading}
        onCancel={backToList}
        onSuccess={backToList}
      />
    );
  }

  return (
    <>
      <AgentConfigsList
        configs={existingConfigs}
        isLoading={isConfigsLoading}
        isError={isConfigsError}
        onEdit={(row) => {
          setSelected(row);
          setView("edit");
        }}
      />
      <ModalButtonsContainer spaceBetween>
        <TextButton
          type="button"
          data-testid="account-configs-close"
          label="Close"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            closeModal();
          }}
        />
        <TextButton
          type="button"
          buttonStyle="primary"
          data-testid="account-configs-new"
          label="+ New Config"
          onClick={() => setView("create")}
        />
      </ModalButtonsContainer>
    </>
  );
};

export default AgentConfigsSection;
